import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import TextField, { TextFieldProps } from '@mui/material/TextField';

const CAPS_LOCK_WARNING = 'CapsLockが有効です';

const PasswordTextField = React.forwardRef<HTMLDivElement, TextFieldProps>(
  (
    {
      helperText,
      onBlur,
      onClick,
      onKeyDown,
      onKeyUp,
      onMouseDown,
      type = 'password',
      ...props
    },
    ref
  ) => {
    const [isCapsLockOn, setIsCapsLockOn] = useState(false);
    const isMaskedPassword = type === 'password';

    useEffect(() => {
      if (!isMaskedPassword) {
        setIsCapsLockOn(false);
      }
    }, [isMaskedPassword]);

    const updateCapsLockState = (event: React.KeyboardEvent | React.MouseEvent) => {
      if (!isMaskedPassword) {
        setIsCapsLockOn(false);
        return;
      }

      setIsCapsLockOn(event.getModifierState('CapsLock'));
    };

    const handleMouseDown: NonNullable<TextFieldProps['onMouseDown']> = (event) => {
      updateCapsLockState(event);
      onMouseDown?.(event);
    };

    const handleClick: NonNullable<TextFieldProps['onClick']> = (event) => {
      updateCapsLockState(event);
      onClick?.(event);
    };

    const handleKeyDown: NonNullable<TextFieldProps['onKeyDown']> = (event) => {
      updateCapsLockState(event);
      onKeyDown?.(event);
    };

    const handleKeyUp: NonNullable<TextFieldProps['onKeyUp']> = (event) => {
      updateCapsLockState(event);
      onKeyUp?.(event);
    };

    const handleBlur: NonNullable<TextFieldProps['onBlur']> = (event) => {
      setIsCapsLockOn(false);
      onBlur?.(event);
    };

    const mergedHelperText = isMaskedPassword && isCapsLockOn ? (
      <>
        <Box component="span" sx={{ color: 'warning.main', display: 'block' }}>
          {CAPS_LOCK_WARNING}
        </Box>
        {helperText && (
          <Box component="span" sx={{ display: 'block' }}>
            {helperText}
          </Box>
        )}
      </>
    ) : (
      helperText
    );

    return (
      <TextField
        {...props}
        ref={ref}
        type={type}
        helperText={mergedHelperText}
        onBlur={handleBlur}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onMouseDown={handleMouseDown}
      />
    );
  }
);

PasswordTextField.displayName = 'PasswordTextField';

export default PasswordTextField;
